import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Reporter } from '@pf/shared';
import { usePublication, usePublicationReportersInfinite } from '../hooks/usePublications';
import { useCreateContact } from '../hooks/useContacts';

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return (parts[0]?.[0] ?? '').toUpperCase();
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase();
}

function PublicationFavicon({ url, size = 'lg' }: { url: string | null; name: string; size?: 'sm' | 'lg' }) {
  const [error, setError] = useState(false);
  const cls = size === 'lg' ? 'w-16 h-16 rounded-xl' : 'w-8 h-8 rounded-md';

  if (url && !error) {
    return (
      <img
        src={url}
        alt=""
        className={`${cls} object-contain flex-shrink-0`}
        onError={() => setError(true)}
      />
    );
  }
  return (
    <div className={`${cls} bg-gray-100 flex items-center justify-center flex-shrink-0`}>
      <svg
        className={size === 'lg' ? 'w-8 h-8 text-gray-400' : 'w-4 h-4 text-gray-400'}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
      </svg>
    </div>
  );
}

function ReporterCard({
  reporter,
  searchQuery,
  onImport,
  importing,
}: {
  reporter: Reporter;
  searchQuery: string;
  onImport: (id: string, name: string) => void;
  importing: string | null;
}) {
  const navigate = useNavigate();
  const topBeats = reporter.beats.slice(0, 3);
  const isImporting = importing === reporter.id;

  function highlight(text: string) {
    if (!searchQuery || searchQuery.length < 2) return text;
    const escaped = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === searchQuery.toLowerCase() ? (
        <mark key={i} className="bg-yellow-100 text-gray-900 rounded-sm px-0.5">{part}</mark>
      ) : (
        <React.Fragment key={i}>{part}</React.Fragment>
      ),
    );
  }

  return (
    <div
      className="bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-sm cursor-pointer transition-all"
      onClick={() => navigate(`/reporters/${reporter.id}`)}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {reporter.imageUrl ? (
            <img
              src={reporter.imageUrl}
              alt={reporter.name}
              className="w-11 h-11 rounded-full object-cover flex-shrink-0"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          ) : (
            <div className="w-11 h-11 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm flex items-center justify-center flex-shrink-0 select-none">
              {getInitials(reporter.name)}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-gray-900 leading-tight truncate">
              {highlight(reporter.name)}
            </h3>
            {reporter.title && (
              <p className="text-xs text-gray-500 mt-0.5 truncate">{reporter.title}</p>
            )}
          </div>
        </div>

        {topBeats.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {topBeats.map((beat) => (
              <span key={beat} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                {beat}
              </span>
            ))}
            {reporter.beats.length > 3 && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">
                +{reporter.beats.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between items-center">
          {reporter.email ? (
            <span className="text-xs text-gray-400 truncate max-w-[120px]" title={reporter.email}>
              {reporter.email}
            </span>
          ) : (
            <span className="text-xs text-gray-300">No email</span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onImport(reporter.id, reporter.name);
            }}
            disabled={isImporting}
            className="text-xs bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 px-2.5 py-1 rounded font-medium text-gray-700 transition-colors flex-shrink-0 ml-2"
          >
            {isImporting ? 'Adding...' : 'Add to Contacts'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PublicationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError } = usePublication(id);
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 400);
  const [importing, setImporting] = useState<string | null>(null);
  const createContact = useCreateContact();

  const {
    data: reportersData,
    isLoading: reportersLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = usePublicationReportersInfinite(id, {
    q: debouncedSearch || undefined,
  });

  const pub = data?.data;
  const allReporters = reportersData?.pages.flatMap((p) => p.data) ?? [];
  const reportersTotal = reportersData?.pages[0]?.total ?? 0;

  async function handleImport(reporterId: string, reporterName: string) {
    setImporting(reporterId);
    try {
      const nameParts = reporterName.trim().split(/\s+/);
      const firstName = nameParts[0] ?? '';
      const lastName = nameParts.slice(1).join(' ') || '';
      const result = await createContact.mutateAsync({
        firstName,
        lastName: lastName || firstName,
        reporterId,
      });
      navigate(`/contacts/${result.data.id}`);
    } catch {
      // user can retry
    } finally {
      setImporting(null);
    }
  }

  if (isLoading) {
    return <div className="p-6 text-sm text-gray-500">Loading...</div>;
  }

  if (isError || !pub) {
    return (
      <div className="p-6">
        <p className="text-sm text-red-600">Failed to load publication.</p>
        <button
          onClick={() => navigate('/publications')}
          className="mt-2 text-sm text-blue-600 hover:underline"
        >
          Back to Publications
        </button>
      </div>
    );
  }

  const emailFormats = Array.isArray(pub.emailFormats) ? pub.emailFormats as string[] : null;

  return (
    <div className="p-6 max-w-4xl space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm">
        <button
          onClick={() => navigate('/publications')}
          className="text-gray-500 hover:text-gray-900 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Publications
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-gray-900 font-medium">{pub.name}</span>
      </div>

      {/* Header card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start gap-4">
          <PublicationFavicon url={pub.faviconUrl} name={pub.name} size="lg" />
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-semibold text-gray-900">{pub.name}</h1>
            {pub.url && (
              <a
                href={pub.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline mt-0.5 inline-flex items-center gap-1"
              >
                {pub.url}
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )}
            {pub.description && (
              <p className="text-sm text-gray-600 mt-2 leading-relaxed">{pub.description}</p>
            )}
            {pub.categories.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {pub.categories.map((cat) => (
                  <span key={cat} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                    {cat}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex-shrink-0 text-right">
            <div className="text-2xl font-bold text-gray-900">
              {pub.reportersCount.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500">reporters</div>
          </div>
        </div>
      </div>

      {/* Audience metrics */}
      {(pub.pageRank !== null || pub.audienceSizeEstimate || pub.monthlyArticleCount !== null) && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-4">Audience Metrics</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {pub.pageRank !== null && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Domain Authority</p>
                <div className="flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-gray-900">
                    {Math.round((pub.pageRank ?? 0) * 10) / 10}
                  </span>
                  <span className="text-xs text-gray-400">/10</span>
                </div>
                <div className="mt-1.5 w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full bg-blue-500"
                    style={{ width: `${Math.min(100, ((pub.pageRank ?? 0) / 10) * 100)}%` }}
                  />
                </div>
              </div>
            )}
            {pub.audienceSizeEstimate && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Est. Audience Size</p>
                <span className="text-lg font-semibold text-gray-900">{pub.audienceSizeEstimate}</span>
                <p className="text-xs text-gray-400 mt-0.5">monthly readers</p>
              </div>
            )}
            {pub.monthlyArticleCount !== null && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Monthly Articles</p>
                <span className="text-2xl font-bold text-gray-900">
                  {(pub.monthlyArticleCount ?? 0).toLocaleString()}
                </span>
                <p className="text-xs text-gray-400 mt-0.5">avg/month</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email formats */}
      {emailFormats && emailFormats.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-3">Email Format Patterns</h2>
          <div className="space-y-2">
            {emailFormats.map((pattern, i) => (
              <div key={i} className="flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <code className="text-sm text-gray-700 font-mono bg-gray-50 px-2 py-0.5 rounded">
                  {pattern}
                </code>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reporters at this publication */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Reporters
            {reportersTotal > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({reportersTotal.toLocaleString()})
              </span>
            )}
          </h2>
        </div>

        {/* Search within publication */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            placeholder="Search reporters at this publication..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full border border-gray-300 rounded-lg pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {reportersLoading && (
          <div className="py-12 text-center text-sm text-gray-500">Loading reporters...</div>
        )}

        {!reportersLoading && allReporters.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-sm text-gray-500">
              {debouncedSearch
                ? 'No reporters match your search.'
                : 'No reporters found at this publication.'}
            </p>
          </div>
        )}

        {!reportersLoading && allReporters.length > 0 && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {allReporters.map((reporter) => (
                <ReporterCard
                  key={reporter.id}
                  reporter={reporter}
                  searchQuery={debouncedSearch}
                  onImport={handleImport}
                  importing={importing}
                />
              ))}
            </div>
            <div className="flex flex-col items-center gap-3">
              <p className="text-xs text-gray-400">
                Showing {allReporters.length.toLocaleString()} of {reportersTotal.toLocaleString()} reporters
              </p>
              {hasNextPage && (
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="px-5 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-gray-700 transition-colors"
                >
                  {isFetchingNextPage ? 'Loading...' : 'Load more'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
